import { useMarketRangeDates } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMarketRangeDates";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { getFaireMarketUtils } from "@faire/web/common/promotional-events/getFaireMarketUtils";
import { Row } from "@faire/web/slate/Layout";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { eventsThemeColors } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeLogo } from "@faire/web/slate/Theme/EventsThemeLogo";
import { EventsThemeShape } from "@faire/web/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

export const BrandsLandingPageFaireMarketShowcase: React.FC = () => {
  return (
    <ResponsiveContentV2
      mobile={
        <MobileContainer>
          <StyledMarketBadge />
          <Spacer size="2x" />
          <TextContainer />
        </MobileContainer>
      }
      tabletAndAbove={
        <Container>
          <LogoContainer />
          <Spacer size="3x" />
          <TextContainer />
        </Container>
      }
    />
  );
};

const LogoContainer: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { getLocalizedDateIncludingFrom } = getFaireMarketUtils(
    undefined,
    strictLocalize
  );
  const { latestMarketEarlyAccessRangeDate } = useMarketRangeDates();
  return (
    <LogoContainerWrapper>
      <StyledMarketPrimaryLogo />
      <Spacer size="6x" />
      <Typography
        variant={{
          mobileAndAbove: "paragraphSansMedium",
          tabletAndAbove: "sectionHeaderSansMedium",
        }}
        color={eventsThemeColors.faireMarket.promoText}
      >
        {getLocalizedDateIncludingFrom(
          latestMarketEarlyAccessRangeDate,
          strictLocalize
        )}
      </Typography>
    </LogoContainerWrapper>
  );
};

const TextContainer = () => {
  return (
    <TextContainerWrapper>
      <Typography
        variant={{
          mobileAndAbove: "subheadingSansMedium",
          tabletAndAbove: "displayLSerifRegular",
        }}
      >
        <StrictLocalMsg
          id="68LOSn"
          defaultMessage="Showcase your brand at Faire Markets"
          description={{
            text: "Telling the brand to showcase at the Faire Markets event",
          }}
        />
      </Typography>
      <Spacer size={{ mobileAndAbove: "1x", tabletAndAbove: "2x" }} />
      <Typography
        color={Core.text.subdued}
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          tabletAndAbove: "subheadingSansRegular",
        }}
      >
        <StrictLocalMsg
          id="1LUH98"
          defaultMessage="Our community comes together twice a year during Faire’s online trade show events to buy, sell, and build new relationships around the world."
        />
      </Typography>
    </TextContainerWrapper>
  );
};

const MobileContainer = styled(Row)`
  padding: ${getSpacing(0, "3x", "4x")};
`;

const Container = styled(Row).attrs({
  align: "center",
  justify: "center",
})`
  margin-bottom: ${getSpacing(96)};
  padding: ${getSpacing(0, "5x", "3x")};
  height: fit-content;
  width: 100%;

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x", "3x", "2x")};
  }
`;

const LogoContainerWrapper = styled(Column)`
  background: ${eventsThemeColors.faireMarket.lightBackground};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("9x", "8x", 88)};
    width: 430px;
  }

  ${responsiveMediaQueries.mobile} {
    margin: ${getSpacing(0, 55)};
    padding: ${getSpacing("4x", "5x")};
  }
`;

const TextContainerWrapper = styled(Column)`
  ${responsiveMediaQueries.tabletAndAbove} {
    padding-left: ${getSpacing("9x")};
    width: 455px;
  }
`;

const StyledMarketPrimaryLogo = styled(EventsThemeLogo.faireMarket.primary)`
  width: 187px;
  height: 150px;

  ${responsiveMediaQueries.mobile} {
    width: 93px;
    height: 75px;
  }
`;

const StyledMarketBadge = styled(EventsThemeShape.faireMarket.badge).attrs({
  width: "24px",
  height: "24px",
})`
  margin-top: 4px;
`;
